import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { 
  ArrowLeft, Bookmark, BookmarkCheck, Lock, 
  Type, Braces, Palette, Sparkles, Search, Mail, 
  Terminal, ImageDown, CheckCircle2, ArrowRight, Code, Repeat,
  Calculator
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import SEOHead from '../components/SEOHead';
import StructuredData from '../components/StructuredData';
import Breadcrumb from '../components/Breadcrumb';
import { getToolSEO } from '../data/toolSEOData';
import { getToolSEOContent } from '../data/toolSEOContent';
import { getRelatedTools, getToolCategory } from '../data/toolRelations';
import { toolComponents, hasToolComponent } from '../data/toolRegistry';
import ReactMarkdown from 'react-markdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Debug: Log available tools in registry
if (process.env.NODE_ENV === 'development') {
  console.log('[ToolDetail] Available tools in registry:', Object.keys(toolComponents).length);
}

const iconMap = {
  type: Type,
  braces: Braces,
  palette: Palette,
  sparkles: Sparkles,
  search: Search,
  mail: Mail,
  terminal: Terminal,
  'image-down': ImageDown,
  code: Code,
  repeat: Repeat,
  calculator: Calculator,
};

const tierColors = {
  free: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  pro: 'bg-primary/20 text-primary border-primary/30',
  business: 'bg-accent/20 text-accent border-accent/30',
};

const ToolDetail = () => {
  const { slug } = useParams();
  const { user, login } = useAuth();
  const [tool, setTool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [relatedTools, setRelatedTools] = useState([]);

  useEffect(() => {
    const fetchTool = async () => {
      // Debug: Log the slug from URL
      console.log('[ToolDetail] URL slug:', slug);
      console.log('[ToolDetail] Is tool registered?', hasToolComponent(slug));
      
      try {
        const response = await axios.get(`${API}/tools/${slug}`);
        const toolData = response.data;
        
        // Debug: Log API response
        console.log('[ToolDetail] API response slug:', toolData?.slug);
        console.log('[ToolDetail] API response name:', toolData?.name);
        
        // Ensure slug consistency - use URL slug as the source of truth
        if (toolData) {
          toolData.slug = slug; // Force slug to match URL
        }
        
        setTool(toolData);
        
        if (user?.saved_tools?.includes(toolData.tool_id)) {
          setIsSaved(true);
        }
        
        // Debug: Check if component exists
        const hasComponent = toolComponents[slug];
        console.log('[ToolDetail] Component found for slug?', !!hasComponent);
        
      } catch (error) {
        console.error('[ToolDetail] Error fetching tool:', error);
        console.log('[ToolDetail] Falling back to registry-only mode for slug:', slug);
        
        // Fallback: If API fails but tool is in registry, create minimal tool object
        if (hasToolComponent(slug)) {
          console.log('[ToolDetail] Tool found in registry, creating fallback');
          setTool({
            slug: slug,
            name: slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
            short_description: 'Tool loaded from registry',
            tier: 'free',
            icon: 'code'
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTool();
  }, [slug, user]);

  // Fetch related tools when slug changes
  useEffect(() => {
    const fetchRelatedTools = async () => {
      const relatedSlugs = getRelatedTools(slug);
      if (relatedSlugs.length === 0) {
        setRelatedTools([]);
        return;
      }
      try {
        const response = await axios.get(`${API}/tools`);
        const related = response.data.filter(t => relatedSlugs.includes(t.slug));
        setRelatedTools(related);
      } catch (error) {
        console.error('Error fetching related tools:', error);
        setRelatedTools([]);
      }
    };
    fetchRelatedTools();
  }, [slug]);

  const handleSave = async () => {
    if (!user) {
      login();
      return;
    }

    try {
      if (isSaved) {
        await axios.post(`${API}/user/unsave-tool`, { tool_id: tool.tool_id }, { withCredentials: true });
        setIsSaved(false);
        toast.success('Tool removed from saved');
      } else {
        await axios.post(`${API}/user/save-tool`, { tool_id: tool.tool_id }, { withCredentials: true });
        setIsSaved(true);
        toast.success('Tool saved');
      }
    } catch (error) {
      toast.error('Failed to update saved tools');
    }
  };

  const canUseTool = () => {
    if (!tool) return false;
    if (tool.tier === 'free') return true;
    if (!user) return false;
    if (user.subscription === 'business') return true;
    if (user.subscription === 'pro' && (tool.tier === 'pro' || tool.tier === 'free')) return true;
    return false;
  };

  // Get SEO data
  const seoData = tool ? getToolSEO(slug, tool) : null;
  const seoContent = tool ? getToolSEOContent(slug, tool.name, tool.short_description) : null;
  
  // STRICT component lookup - use URL slug directly
  const ToolInterface = toolComponents[slug];
  const IconComponent = tool ? iconMap[tool.icon] : null;
  
  // ALWAYS log component lookup for debugging (in production too)
  console.log('[ToolDetail] COMPONENT LOOKUP:', {
    slug: slug,
    componentExists: !!ToolInterface,
    componentName: ToolInterface ? ToolInterface.name || ToolInterface.displayName || 'Component' : 'NOT_FOUND',
    componentType: typeof ToolInterface,
    registryKeys: Object.keys(toolComponents).filter(k => k.includes('calculator')).join(', ')
  });
  
  // Error if component not found for calculator tools
  if (slug && slug.includes('calculator') && !ToolInterface) {
    console.error('[ToolDetail] ERROR: Calculator component not found for slug:', slug);
    console.error('[ToolDetail] Available calculator slugs:', Object.keys(toolComponents).filter(k => k.includes('calculator')));
  }

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          <Skeleton className="h-8 w-32 mb-8" />
          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <Skeleton className="w-16 h-16 rounded-lg mb-4" />
              <Skeleton className="h-10 w-3/4 mb-4" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-2/3" />
            </div>
            <Skeleton className="h-96 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!tool) {
    // Check if tool exists in registry even if API failed
    const hasRegistryComponent = hasToolComponent(slug);
    
    return (
      <div className="min-h-screen pt-24 pb-16 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-2xl font-bold mb-4">Tool Not Found</h1>
          <p className="text-muted-foreground mb-4">
            The tool "{slug}" could not be loaded.
          </p>
          {process.env.NODE_ENV === 'development' && (
            <div className="text-left bg-muted p-4 rounded-lg mb-4 max-w-md mx-auto">
              <p className="text-sm font-mono">Debug Info:</p>
              <p className="text-sm font-mono">- URL Slug: {slug}</p>
              <p className="text-sm font-mono">- In Registry: {hasRegistryComponent ? 'Yes' : 'No'}</p>
              <p className="text-sm font-mono">- API Status: Failed</p>
            </div>
          )}
          <Link to="/tools">
            <Button>Browse All Tools</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* SEO Meta Tags */}
      <SEOHead
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={`${window.location.origin}/tools/${slug}`}
      />

      {/* Structured Data (JSON-LD) */}
      <StructuredData tool={tool} seoData={seoData} />

      <div className="min-h-screen pt-24 pb-16 px-6" data-testid="tool-detail-page">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumb Navigation */}
          <Breadcrumb 
            items={[
              { label: 'Home', href: '/' },
              { label: 'Tools', href: '/tools' },
              { label: getToolCategory(slug), href: `/tools?category=${encodeURIComponent(getToolCategory(slug))}` },
              { label: tool.name }
            ]}
          />

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left Column - Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Icon & Title */}
              <div className="flex items-start gap-4 mb-6">
                {IconComponent && (
                  <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center">
                    <IconComponent className="w-8 h-8 text-primary" />
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-3xl font-bold font-['Plus_Jakarta_Sans']" data-testid="tool-title">
                      {tool.name}
                    </h1>
                    <Badge variant="outline" className={`${tierColors[tool.tier]} capitalize`}>
                      {tool.tier}
                    </Badge>
                  </div>
                  <p className="text-muted-foreground">{tool.short_description}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 mb-8">
                <Button
                  variant="outline"
                  onClick={handleSave}
                  className="flex items-center gap-2"
                  data-testid="save-tool-btn"
                >
                  {isSaved ? (
                    <>
                      <BookmarkCheck className="w-4 h-4" />
                      Saved
                    </>
                  ) : (
                    <>
                      <Bookmark className="w-4 h-4" />
                      Save Tool
                    </>
                  )}
                </Button>
              </div>

              {/* Description */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold mb-3 font-['Plus_Jakarta_Sans']">About this tool</h2>
                <p className="text-muted-foreground leading-relaxed" data-testid="tool-description">
                  {tool.description}
                </p>
              </div>

              {/* What Is Section - Comprehensive SEO Content */}
              {seoContent?.whatIs && (
                <div className="mb-8">
                  <h2 className="text-xl font-semibold mb-4 font-['Plus_Jakarta_Sans']">
                    What is {tool.name}?
                  </h2>
                  <div className="prose prose-invert prose-sm max-w-none prose-headings:text-foreground prose-headings:font-semibold prose-h2:text-lg prose-h3:text-base prose-p:text-muted-foreground prose-strong:text-foreground prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-ul:text-muted-foreground prose-ol:text-muted-foreground prose-li:marker:text-primary">
                    <ReactMarkdown>{seoContent.whatIs}</ReactMarkdown>
                  </div>
                </div>
              )}

              {/* How to Use - Comprehensive SEO Content */}
              {seoContent?.howToUse?.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-xl font-semibold mb-4 font-['Plus_Jakarta_Sans']">
                    How to Use {tool.name}
                  </h2>
                  <ol className="space-y-3">
                    {seoContent.howToUse.map((step, index) => (
                      <li key={index} className="flex gap-4 p-3 rounded-lg bg-muted/30 border border-border">
                        <span className="w-8 h-8 rounded-full bg-primary/20 text-primary text-sm flex items-center justify-center flex-shrink-0 font-bold">
                          {index + 1}
                        </span>
                        <span className="text-muted-foreground flex items-center">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Use Cases Section */}
              {seoContent?.useCases?.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-xl font-semibold mb-4 font-['Plus_Jakarta_Sans']">
                    Use Cases for {tool.name}
                  </h2>
                  <div className="grid gap-4">
                    {seoContent.useCases.map((useCase, index) => (
                      <div key={index} className="p-4 rounded-lg bg-muted/30 border border-border">
                        <h3 className="font-semibold mb-1 text-foreground">{useCase.title}</h3>
                        <p className="text-sm text-muted-foreground">{useCase.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* How to Use - Enhanced with SEO data (fallback for old data only when no seoContent) */}
              {!seoContent?.howToUse?.length && seoData.usageSteps?.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-xl font-semibold mb-4 font-['Plus_Jakarta_Sans']">How to Use {tool.name}</h2>
                  <div className="space-y-4">
                    {seoData.usageSteps.map((step, index) => (
                      <div key={index} className="flex gap-4 p-4 rounded-lg bg-muted/30 border border-border">
                        <div className="w-10 h-10 rounded-full bg-primary/20 text-primary flex items-center justify-center flex-shrink-0 font-bold">
                          {step.step}
                        </div>
                        <div>
                          <h3 className="font-semibold mb-1">{step.title}</h3>
                          <p className="text-sm text-muted-foreground">{step.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Steps fallback - only show when no seoContent and no seoData */}
              {(!seoContent?.howToUse?.length && (!seoData.usageSteps || seoData.usageSteps.length === 0) && tool.how_it_works?.length > 0) && (
                <div className="mb-8">
                  <h2 className="text-xl font-semibold mb-3 font-['Plus_Jakarta_Sans']">How it works</h2>
                  <ol className="space-y-3">
                    {tool.how_it_works.map((step, index) => (
                      <li key={index} className="flex gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-sm flex items-center justify-center flex-shrink-0">
                          {index + 1}
                        </span>
                        <span className="text-muted-foreground">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Features */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold mb-3 font-['Plus_Jakarta_Sans']">Features</h2>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {seoContent?.features?.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span><strong>100% Free</strong> - No signup required</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span><strong>Instant</strong> results - No waiting</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span><strong>Updated for 2026</strong> - Latest data</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span>Privacy-first - <strong>Data never stored</strong></span>
                  </li>
                </ul>
              </div>

              {/* FAQ - Enhanced with comprehensive SEO content */}
              {(seoContent?.faqs?.length > 0 || seoData.faqs?.length > 0 || tool.faqs?.length > 0) && (
                <div className="mb-8">
                  <h2 className="text-xl font-semibold mb-3 font-['Plus_Jakarta_Sans']">
                    Frequently Asked Questions about {tool.name}
                  </h2>
                  <Accordion type="single" collapsible className="w-full">
                    {(seoContent?.faqs || seoData.faqs || tool.faqs).map((faq, index) => (
                      <AccordionItem key={index} value={`faq-${index}`} className="border-border">
                        <AccordionTrigger className="text-left hover:no-underline">
                          <span className="font-medium">{faq.q}</span>
                        </AccordionTrigger>
                        <AccordionContent className="text-muted-foreground prose prose-invert prose-sm max-w-none prose-a:text-primary prose-a:no-underline hover:prose-a:underline">
                          <ReactMarkdown>{faq.a}</ReactMarkdown>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              )}

              {/* Related Tools placeholder */}
              <div className="p-4 rounded-lg bg-muted/30 border border-border">
                <p className="text-sm text-muted-foreground">
                  <strong>Tip:</strong> Browse more tools in our{' '}
                  <Link to="/tools" className="text-primary hover:underline">tools directory</Link>
                </p>
              </div>
            </motion.div>

            {/* Right Column - Tool Interface */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="sticky top-24">
                <div className="tool-interface rounded-xl p-6" data-testid="tool-interface">
                  {canUseTool() ? (
                    ToolInterface ? (
                      <>
                        {/* RENDERING CUSTOM COMPONENT */}
                        {console.log('[ToolDetail] RENDERING:', ToolInterface.name || ToolInterface.displayName || 'Component', 'for slug:', slug)}
                        <ToolInterface />
                      </>
                    ) : (
                      <div className="text-center py-12" data-testid="component-not-found">
                        <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                          {IconComponent && <IconComponent className="w-8 h-8 text-red-500" />}
                        </div>
                        <h3 className="font-semibold mb-2 text-red-500">Component Not Found</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          No React component registered for slug: <code className="bg-muted px-1 rounded">{slug}</code>
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Check toolRegistry.js mapping
                        </p>
                        {/* Log error in production */}
                        {console.error('[ToolDetail] NO COMPONENT for slug:', slug, '- Check toolRegistry.js')}
                      </div>
                    )
                  ) : (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                        <Lock className="w-8 h-8 text-muted-foreground" />
                      </div>
                      <h3 className="font-semibold mb-2">Upgrade to Access</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        This tool requires a {tool.tier} subscription or higher.
                      </p>
                      <Link to="/pricing">
                        <Button className="glow-blue" data-testid="upgrade-btn">
                          View Pricing
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Related Tools Section - Enhanced for SEO */}
          {relatedTools.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-12"
              data-testid="related-tools-section"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold font-['Plus_Jakarta_Sans']">
                  Related {getToolCategory(slug)}
                </h2>
                <Link 
                  to={`/tools?category=${encodeURIComponent(getToolCategory(slug))}`}
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                >
                  View all {getToolCategory(slug)} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {relatedTools.slice(0, 4).map((relatedTool) => (
                  <Link
                    key={relatedTool.slug}
                    to={`/tools/${relatedTool.slug}`}
                    className="group p-4 rounded-xl border border-border bg-card hover:border-primary/50 hover:bg-card/80 transition-all"
                    data-testid={`related-tool-${relatedTool.slug}`}
                  >
                    <h3 className="font-semibold mb-1 group-hover:text-primary transition-colors">
                      {relatedTool.name}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                      {relatedTool.short_description}
                    </p>
                    <div className="flex items-center text-primary text-sm">
                      Try {relatedTool.name} <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                ))}
              </div>
              
              {/* Additional related tools as text links for SEO */}
              {relatedTools.length > 4 && (
                <div className="mt-4 p-4 rounded-lg bg-muted/30 border border-border">
                  <p className="text-sm text-muted-foreground">
                    <strong className="text-foreground">More tools you might like:</strong>{' '}
                    {relatedTools.slice(4).map((rt, index) => (
                      <span key={rt.slug}>
                        <Link 
                          to={`/tools/${rt.slug}`} 
                          className="text-primary hover:underline"
                        >
                          {rt.name}
                        </Link>
                        {index < relatedTools.slice(4).length - 1 && ', '}
                      </span>
                    ))}
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {/* Browse All Tools CTA - Enhanced */}
          <div className="mt-12 p-6 rounded-xl bg-gradient-to-r from-primary/10 to-accent/10 border border-border text-center">
            <h3 className="text-xl font-bold mb-2 font-['Plus_Jakarta_Sans']">Explore More Free Online Tools</h3>
            <p className="text-muted-foreground mb-4">
              Discover 167+ free online tools for developers, designers, and marketers. No signup required.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/all-tools">
                <Button className="glow-blue">
                  Browse All Tools <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/tools">
                <Button variant="outline">
                  View by Category
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ToolDetail;
